import { Card, CardContent } from "../ui/card";
import {
  CheckCircle,
  Clock,
  Volume2,
  BarChart3,
  MessageSquare,
  XSquare,
  Gauge,
} from "lucide-react";
import type { GetClientsResponse, PercentileStats } from "./types";
import {
  formatDuration,
  formatOptionalDuration,
  isFiniteNumber,
} from "./types";

interface TestSummaryCardsProps {
  summary: GetClientsResponse["summary"];
}

const PercentileCard = ({
  label,
  stats,
}: {
  label: string;
  stats?: PercentileStats;
}) => {
  // Guard every percentile, not just p50 — the backend can return a partial
  // shape (e.g. p50 set, p90/p95 null) and formatDuration(null) would throw.
  const hasData =
    !!stats &&
    stats.p50 != null &&
    stats.p90 != null &&
    stats.p95 != null;
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <Gauge className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-medium text-foreground">{label}</span>
          {hasData && stats?.count != null && (
            <span className="ml-auto text-[0.65rem] text-muted-foreground">
              n={stats.count}
            </span>
          )}
        </div>
        {hasData ? (
          <div className="grid grid-cols-3 gap-2 text-center">
            {(["p50", "p90", "p95"] as const).map((p) => (
              <div key={p} className="flex flex-col">
                <span className="text-[0.65rem] text-muted-foreground uppercase tracking-wider">
                  {p}
                </span>
                <span className="text-sm font-semibold text-foreground">
                  {formatDuration(stats[p] ?? 0)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground italic">No data</span>
        )}
      </CardContent>
    </Card>
  );
};

export const TestSummaryCards = ({ summary }: TestSummaryCardsProps) => {
  const hasCapturedAudio = summary.total_audio_chunks > 0;
  const hasLoginPercentiles = summary.time_to_login_ms?.p50 != null;
  const wsReadyWaitMs =
    summary.avg_ws_ready_wait_ms ?? summary.avg_ws_connect_time_ms;
  const hasWsOpenMetric = isFiniteNumber(summary.avg_ws_create_to_open_ms);
  const hasStartToWsMetric = isFiniteNumber(
    summary.avg_start_click_to_ws_create_ms,
  );
  const hasPercentiles =
    hasLoginPercentiles ||
    [summary.user_perceived_ttfa_ms, summary.audio_gap_ms].some(
      (s) => s?.p50 != null,
    );

  return (
    <>
    <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3 mb-4 max-md:grid-cols-2">
      <Card>
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0">
              <CheckCircle className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-semibold text-foreground">
                {summary.successful_clients}/{summary.total_clients}
              </span>
              <span className="text-xs text-muted-foreground">
                Success Rate
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0">
              <Clock className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-semibold text-foreground">
                {formatDuration(wsReadyWaitMs)}
              </span>
              <span className="text-xs text-muted-foreground">WS Ready</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {hasWsOpenMetric && (
        <Card>
          <CardContent className="p-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0">
                <Clock className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-semibold text-foreground">
                  {formatOptionalDuration(summary.avg_ws_create_to_open_ms)}
                </span>
                <span className="text-xs text-muted-foreground">WS Open</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {hasStartToWsMetric && (
        <Card>
          <CardContent className="p-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0">
                <Clock className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-semibold text-foreground">
                  {formatOptionalDuration(
                    summary.avg_start_click_to_ws_create_ms,
                  )}
                </span>
                <span className="text-xs text-muted-foreground">
                  Start to WS
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0">
              <Volume2 className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-semibold text-foreground">
                {hasCapturedAudio
                  ? formatDuration(summary.avg_time_to_first_audio_ms)
                  : "N/A"}
              </span>
              <span className="text-xs text-muted-foreground">First Audio</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0">
              <BarChart3 className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-semibold text-foreground">
                {formatDuration(summary.avg_total_duration_ms)}
              </span>
              <span className="text-xs text-muted-foreground">
                Avg Duration
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center shrink-0">
              <MessageSquare className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-semibold text-foreground">
                {summary.total_turns_completed}
              </span>
              <span className="text-xs text-muted-foreground">Total Turns</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className={summary.failed_clients > 0 ? "border-destructive" : ""}>
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            <div
              className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${summary.failed_clients > 0 ? "bg-destructive/10" : "bg-muted"}`}
            >
              <XSquare
                className={`h-5 w-5 ${summary.failed_clients > 0 ? "text-destructive" : "text-muted-foreground"}`}
              />
            </div>
            <div className="flex flex-col">
              <span
                className={`text-xl font-semibold ${summary.failed_clients > 0 ? "text-destructive" : "text-muted-foreground"}`}
              >
                {summary.failed_clients}
              </span>
              <span className="text-xs text-muted-foreground">Failed</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    {hasPercentiles && (
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Gauge className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold text-foreground">
            Latency Percentiles
          </h4>
          <span className="text-xs text-muted-foreground">
            (login pooled across clients; others across all turns &times;
            clients)
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
          {hasLoginPercentiles && (
            <PercentileCard
              label="Time to Login"
              stats={summary.time_to_login_ms}
            />
          )}
          <PercentileCard
            label="User-perceived TTFA"
            stats={summary.user_perceived_ttfa_ms}
          />
          <PercentileCard label="Audio Gap" stats={summary.audio_gap_ms} />
        </div>
      </div>
    )}
    </>
  );
};
